import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, CalendarCheck, Users, ArrowRight } from "lucide-react";

const chips = [
  { icon: CalendarCheck, text: "Free trial · book online in 60 seconds", id: "hero-chip-booking" },
  { icon: MapPin, text: "3003 Woodland Ave Pool", id: "hero-chip-location" },
  { icon: Users, text: "Small Groups, Ages 1–12", id: "hero-chip-groups" },
];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onClaim }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yCaption = useTransform(scrollYProgress, [0, 1], [0, -45]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-b from-[#f2ebd9] via-[#f8f3e9] to-[#f8f3e9]"
    >
      <div className="mx-auto grid max-w-6xl gap-14 px-4 sm:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="hero-eyebrow"
            className="inline-flex items-center rounded-full border border-[#c97049]/30 bg-[#e59a6d]/15 px-4 py-1.5 text-xs sm:text-sm font-semibold tracking-wide text-[#c97049]"
          >
            Exclusively for Four Seasons Apartments families
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] text-[#00334a]"
          >
            <MaskedLine delay={0.15}>Swim lessons your</MaskedLine>
            <MaskedLine delay={0.28}>kids will beg for —</MaskedLine>
            <MaskedLine delay={0.41} className="italic text-[#c97049]">
              steps from your door.
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-testid="hero-subheadline"
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#00334a]/75"
          >
            No traffic. No cross-town drives. Coach Abrie, our Program Director, and her
            caring instructors teach right at your community pool — and as a resident you
            get a free trial lesson plus 15% off every session.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.62 }}
            className="mt-9"
          >
            <button
              type="button"
              data-testid="hero-claim-trial-button"
              onClick={onClaim}
              className="group inline-flex items-center gap-3 rounded-full bg-[#c97049] px-8 py-4 text-base sm:text-lg font-semibold text-white shadow-lg shadow-[#c97049]/30 hover:bg-[#b25f3a] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              Book My Free Trial Lesson
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {chips.map((c) => (
              <span
                key={c.id}
                data-testid={c.id}
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-[#00334a]/70"
              >
                <c.icon className="h-4 w-4 text-[#c97049]" />
                {c.text}
              </span>
            ))}
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ y: yPhoto }}
          className="relative"
        >
          <div className="absolute -inset-5 rounded-[2.5rem] bg-[#ddc0a6]/50 -rotate-2" aria-hidden="true" />
          <motion.div
            initial={{ clipPath: "inset(14% 14% 14% 14% round 2rem)", opacity: 0.3 }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 2rem)", opacity: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="hero-photo-tilt relative overflow-hidden rounded-[2rem] shadow-2xl shadow-[#00334a]/25"
          >
            <img
              src="/photos/IMG_9505.jpeg"
              alt="Coach Abrie laughing face to face with a toddler during a lesson at the community pool"
              data-testid="hero-photo"
              className="h-[420px] sm:h-[540px] w-full object-cover object-[50%_30%]"
            />
          </motion.div>
          <motion.div
            style={{ y: yCaption }}
            data-testid="hero-photo-caption"
            className="absolute -bottom-5 left-5 right-5 sm:right-auto rounded-2xl bg-white/95 backdrop-blur px-5 py-3.5 shadow-lg border border-[#00334a]/10"
          >
            <p className="text-sm font-semibold text-[#00334a]">Real lessons at your own pool</p>
            <p className="text-xs text-[#52656d] mt-0.5">Coached by the Little Splash team you already see around the property</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
